import React from "react";
import { Popover, Button, List, ListItem, Grid, FormHelperText } from "@material-ui/core";
import { DateTimePicker } from "./DateTimePicker";
import moment from "moment";

interface DateTimeFilterProps {
	//The date to be displayed in start date.
	startDate: Date;
	//The date to be displayed as end date.
	endDate: Date;
	//The function passed into by filter container to set the dates for the page.
	setDates: (startDate: Date, endDate: Date) => void;
	//The function to be passed in so that the number of hours to be set as the offset of start and end date can be set in filter container.
	setRefreshThresholdFunction: (numberOfHours: number) => void;
	//The starting timespan between start and end date.
	refreshDateTimespan: number;
}

const LAST_30_MINS_LABEL = "Last 30 Minutes";
const LAST_1_HOUR_LABEL = "Last Hour";
const LAST_4_HOURS_LABEL = "Last 4 hours";
const LAST_12_HOURS_LABEL = "Last 12 Hours";
const LAST_24_HOURS_LABEL = "Last 24 Hours";
const LAST_72_HOURS_LABEL = "Last 72 Hours";
const CUSTOM_LABEL = "Custom";

/**
 * Accepts a start date and an end date as well as a callback
 * function for sending the new dates back as well as a callback for setting the interval between start and end date in hours.
 * If the dates are set as custom refresh interval is set to 0.
 * @param props
 */
export const DateTimeFilter = (props: DateTimeFilterProps) => {
	let startingLabel = CUSTOM_LABEL;

	switch (props.refreshDateTimespan) {
		case 0.5:
			startingLabel = LAST_30_MINS_LABEL;
			break;
		case 1:
			startingLabel = LAST_1_HOUR_LABEL;
			break;
		case 4:
			startingLabel = LAST_4_HOURS_LABEL;
			break;
		case 12:
			startingLabel = LAST_12_HOURS_LABEL;
			break;
		case 24:
			startingLabel = LAST_24_HOURS_LABEL;
			break;
		case 72:
			startingLabel = LAST_72_HOURS_LABEL;
			break;
		default:
			break;
	}
	const [startDate, setStartDate] = React.useState(props.startDate);
	const [endDate, setEndDate] = React.useState(props.endDate);
	const [label, setLabel] = React.useState(startingLabel);
	const [customOpen, setCustomOpen] = React.useState(false);
	const [startDateHelperMessage, setStartDateHelperMessage] = React.useState("");
	const [endDateHelperMessage, setEndDateHelperMessage] = React.useState("");
	const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);

	// sets label for date time filter menu when filters are reset
	if (props.refreshDateTimespan && label !== startingLabel) {
		setLabel(startingLabel);
	}
	// resets date picker to closed if not set to custom timespan
	if (startingLabel !== CUSTOM_LABEL && customOpen == true) {
		setCustomOpen(false);
	}

	const [pickerHelperMessage, setPickerHelperMessage] = React.useState<string>(
		moment(endDate).startOf("day") < moment().add(-30, "days").startOf("day") ||
			moment(startDate).startOf("day") < moment().add(-30, "days").startOf("day")
			? "Filter date is more than 30 days in the past."
			: "",
	);
	const handleStartDateChange = (newStartDate: Date) => {
		if (newStartDate < moment().subtract(30, "days").startOf("day").toDate()) {
			newStartDate = moment().subtract(30, "days").startOf("day").toDate();
			setStartDateHelperMessage("Start Date cannot be more than 30 days in the past");
		} else if (newStartDate > endDate) {
			newStartDate = endDate;
			setStartDateHelperMessage("Start Date cannot be more recent than End Date.");
		} else {
			setStartDateHelperMessage("");
		}
		setStartDate(newStartDate);
	};

	const handleEndDateChange = (newEndDate: Date) => {
		if (newEndDate > moment().toDate()) {
			newEndDate = moment().toDate();
			setEndDateHelperMessage("End Date cannot be in the future");
		} else if (startDate > newEndDate) {
			newEndDate = startDate;
			setEndDateHelperMessage("End Date cannot be prior than Start Date.");
		} else {
			setEndDateHelperMessage("");
		}
		setEndDate(newEndDate);
	};

	const setPastDateFilterRange = (hours: number, label: string) => {
		const sDate = moment().subtract(hours, "hours").toDate();
		const eDate = moment().toDate();
		setStartDate(sDate);
		setEndDate(eDate);
		setLabel(label);
		setCustomOpen(false);
		props.setDates(sDate, eDate);
		props.setRefreshThresholdFunction(hours);
		setAnchorEl(null);
	};

	const lastHalfHour = () => setPastDateFilterRange(0.5, LAST_30_MINS_LABEL);
	const lastHour = () => setPastDateFilterRange(1, LAST_1_HOUR_LABEL);
	const lastFourHours = () => setPastDateFilterRange(4, LAST_4_HOURS_LABEL);
	const lastTwelveHours = () => setPastDateFilterRange(12, LAST_12_HOURS_LABEL);
	const lastTwentyFourHours = () => setPastDateFilterRange(24, LAST_24_HOURS_LABEL);
	const lastSeventyTwoHours = () => setPastDateFilterRange(72, LAST_72_HOURS_LABEL);

	const lastCustomHours = () => {
		setLabel(CUSTOM_LABEL);
		setCustomOpen(true);
		props.setRefreshThresholdFunction(0);
	};

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
		props.setDates(startDate, endDate);
	};

	const open = Boolean(anchorEl);
	const id = open ? "date-popover" : undefined;

	if (
		pickerHelperMessage.length > 0 &&
		moment(endDate).startOf("day") > moment().add(-30, "days").startOf("day") &&
		moment(startDate).startOf("day") > moment().add(-30, "days").startOf("day")
	) {
		setPickerHelperMessage("");
	}

	// console.log(`end date: ${eDate}`);
	// console.log(`start date: ${sDate}`);
	// console.log(`test date: ${testDate}`);
	return (
		<div>
			<Button size="small" variant="outlined" onClick={handleClick}>
				{`Time Range: ${label}`}
			</Button>
			{pickerHelperMessage.length > 0 && <FormHelperText error={true}>{pickerHelperMessage}</FormHelperText>}
			<Popover
				action={(actions) => actions && actions.updatePosition()}
				id={id}
				open={open}
				anchorEl={anchorEl}
				onClose={handleClose}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
			>
				<Grid container wrap={"nowrap"}>
					<Grid item>
						<List style={{ width: 150 }}>
							<ListItem button selected={label == LAST_30_MINS_LABEL} onClick={lastHalfHour}>
								{LAST_30_MINS_LABEL}
							</ListItem>
							<ListItem button selected={label == LAST_1_HOUR_LABEL} onClick={lastHour}>
								{LAST_1_HOUR_LABEL}
							</ListItem>
							<ListItem button selected={label == LAST_4_HOURS_LABEL} onClick={lastFourHours}>
								{LAST_4_HOURS_LABEL}
							</ListItem>
							<ListItem button selected={label == LAST_12_HOURS_LABEL} onClick={lastTwelveHours}>
								{LAST_12_HOURS_LABEL}
							</ListItem>
							<ListItem button selected={label == LAST_24_HOURS_LABEL} onClick={lastTwentyFourHours}>
								{LAST_24_HOURS_LABEL}
							</ListItem>
							<ListItem button selected={label == LAST_72_HOURS_LABEL} onClick={lastSeventyTwoHours}>
								{LAST_72_HOURS_LABEL}
							</ListItem>
							<ListItem button selected={label == CUSTOM_LABEL} onClick={lastCustomHours}>
								{CUSTOM_LABEL}
							</ListItem>
						</List>
					</Grid>
					<Grid item style={{ overflowX: "auto" }}>
						<span hidden={!customOpen} style={{ whiteSpace: "nowrap" }}>
							<span style={{ marginRight: 10 }}>
								<DateTimePicker
									datePickerLabel={"Start Date (UTC)"}
									selectedDate={startDate}
									maximumDate={endDate}
									minimumDate={moment().subtract(30, "days").startOf("day").toDate()}
									setDate={handleStartDateChange}
									helperMessage={startDateHelperMessage}
								></DateTimePicker>
							</span>
							<span>
								<DateTimePicker
									datePickerLabel={"End Date (UTC)"}
									selectedDate={endDate}
									minimumDate={startDate}
									setDate={handleEndDateChange}
									helperMessage={endDateHelperMessage}
								></DateTimePicker>
							</span>
						</span>
					</Grid>
				</Grid>
			</Popover>
		</div>
	);
};
